import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import CommonImg from "../Common/Img";
import Bell from "@/assets/images/bell.png";
import { ArrowRight, X } from "lucide-react";
import { useReadAlerts } from "@/libs/react-query/hooks/useAlertQuery";
import { AlertObject } from "@/types/alert";
import { cn, formatDate } from "@/libs/utils";
import { showToast } from "@/utils/toastUtils";
import { readProjectGroupsByTaskId } from "@/services/api/project";
import { ReadProjectGroupsByTaskIdResponse } from "@/types/project";

export default function NotificationPopover() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [localAlerts, setLocalAlerts] = useState<AlertObject[]>([]);
  const firstOpen = useRef(true);

  const onClose = () => {
    setOpen(false);
  };

  const { data, refetch } = useReadAlerts({});
  const { alerts = [] } = data || {};
  const unseenAlerts = useMemo(
    () => alerts.filter((alert: AlertObject) => !alert.seen_yn),
    [alerts]
  );

  useEffect(() => {
    if (open) {
      refetch();

      if (firstOpen.current && alerts.length > 0) {
        setLocalAlerts(alerts);
        firstOpen.current = false;
      }
    } else {
      if (data?.alerts) {
        setLocalAlerts(data.alerts);
      }
    }
  }, [open]);

  const handleNavigate = async ({ route_url }: AlertObject) => {
    if (!route_url) return;

    const isTaskRoute = route_url.startsWith("/management/tasks/");
    if (isTaskRoute) {
      const taskId = route_url.split("/").pop();
      if (!taskId) return;

      try {
        const { project_group_id }: ReadProjectGroupsByTaskIdResponse =
          await readProjectGroupsByTaskId({ task_id: taskId });
        if (project_group_id) {
          navigate(`/management/tasks/${taskId}`, {
            state: {
              group_id: project_group_id,
            },
          });
        }
      } catch (error) {
        showToast({
          message: "해당 작업을 찾을 수 없습니다.",
          type: "error",
        });
      }
    } else {
      navigate(route_url);
    }
    onClose();
  };

  function renderAlertMessage(alert: AlertObject) {
    const { message, send_user_id } = alert;
    const textClassName = "text-xs text-gray-A font-normal";

    if (send_user_id === "system") {
      return <span className={textClassName}>{message}</span>;
    }

    const match = message.match(/^\[([^\]]+)\](.+)/);

    if (!match) {
      return <span className={textClassName}>{message}</span>;
    }

    const [, name, rest] = match;

    return (
      <span className={textClassName}>
        <strong className="pr-1">{name}</strong>
        {rest}
      </span>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger>
        <div className="relative">
          <CommonImg src={Bell} alt="Bell Icon" className="h-[24px] w-[24px]" />
          {unseenAlerts.length > 0 && (
            <div className="absolute -top-2 -right-2 w-5 h-5 bg-dark-red rounded-full flex items-center justify-center">
              <p className="text-white text-[12px] font-semibold">
                {unseenAlerts.length}
              </p>
            </div>
          )}
        </div>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        side="right"
        sideOffset={4}
        alignOffset={4}
        className="w-[464px] max-h-[553px] overflow-y-auto p-8 flex flex-col gap-4"
      >
        <div className="relative">
          <p className="text-2xl text-gray-A font-bold text-center">알림</p>
          <X
            className="absolute top-0 right-0 cursor-pointer text-gray-C"
            onClick={onClose}
          />
        </div>
        <div className="flex flex-col gap-2">
          {localAlerts?.map((item: AlertObject) => (
            <div
              key={item.alert_id}
              className={cn(
                "p-4 flex items-center justify-between gap-1 bg-white rounded-lg",
                !item.seen_yn && "bg-gray-E",
                !!item.route_url && "cursor-pointer"
              )}
              onClick={() => handleNavigate(item)}
            >
              <div className="flex flex-col gap-1">
                <p className="text-[10px] text-gray-B font-semibold">
                  {formatDate(item.created_at)}
                </p>
                <div className="flex items-center gap-2">
                  <p className="text-gray-A">{renderAlertMessage(item)}</p>
                </div>
              </div>
              {!!item.route_url && (
                <ArrowRight size={16} className="shrink-0 text-dark-red" />
              )}
            </div>
          ))}
          {localAlerts?.length === 0 && (
            <div className="flex items-center justify-center p-4 bg-white rounded-lg">
              <p className="text-gray-A">알림이 없습니다.</p>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
